/**
 * Daily Reveal Per-Sport Configuration
 *
 * Single source of truth for how Daily Reveal scores, schedules, and optionally
 * runs prop-based gameplay per sport.
 */

import { type SportCode } from './leagues';
import { NFL_DAILY_REVEAL_CONFIG } from './nfl-daily-reveal';
import { NBA_DAILY_REVEAL_CONFIG } from './nba-daily-reveal';

export type DailyRevealMode = 'sport_default' | 'stat_hit' | 'prop_based';
export type ResolvedDailyRevealMode = Exclude<DailyRevealMode, 'sport_default'>;
export type DailyRevealPropOperator = 'over' | 'over_or_equal' | 'over_or_true';
export type DailyRevealPropMarketSource =
  | 'sportsbook_feed'
  | 'manual'
  | 'mock'
  /**
   * Players taken straight from the sports feed's slate rosters, with no prices. Real
   * identities (feed player ids), so picks grade normally — unlike `mock`. Populates a pool for
   * a slate the sportsbook has not priced: preseason, which DK posts no player props for at all,
   * and any week whose props are not up yet.
   */
  | 'feed_roster'
  /**
   * The same real feed-roster players as `feed_roster`, but with SYNTHESISED prices stamped on.
   * Daily Reveal's pack builder sorts and bands on odds, so a price-less pool cannot build a
   * pack at all — hence a separate source rather than stamping prices onto `feed_roster`, whose
   * null-odds contract Survivor depends on.
   *
   * The odds are FABRICATED and carry no market meaning. This source is never operator-selectable
   * (it is absent from DEFAULT_PROP_SOURCES deliberately), is only ever reached automatically when
   * the sportsbook priced nothing for the whole day, and is hard-blocked in production.
   */
  | 'feed_roster_priced'
  | 'historical_replay'
  | 'csv_import';

export interface DailyRevealWeekDayTemplate {
  dayNumber: number;
  categoryKey?: string;
  categoryName?: string;
  gameWindow?: string;
}

export interface DailyRevealWeekConfig {
  type: 'sequential' | 'nfl_tuesday_to_monday' | 'nba_monday_to_sunday';
  timezone?: string;
  days?: DailyRevealWeekDayTemplate[];
}

export interface DailyRevealPropCategoryConfig {
  categoryKey: string;
  categoryName: string;
  propStatType: string;
  operator: DailyRevealPropOperator;
  gameWindow?: string;
  eligiblePositions?: string[];
  lockRule?: string;
}

export interface DailyRevealPropBasedSportConfig {
  categories: DailyRevealPropCategoryConfig[];
  supportedSources: DailyRevealPropMarketSource[];
  defaultSource: DailyRevealPropMarketSource;
  defaultPackSize: number;
  allowPickChangeBeforeLock: boolean;
}

export interface DailyRevealSportConfig {
  /**
   * Deprecated alias retained for backwards compatibility with existing code.
   * Prefer `defaultMode`.
   */
  mode?: ResolvedDailyRevealMode;
  defaultMode?: ResolvedDailyRevealMode;
  weekConfig?: DailyRevealWeekConfig;
  propBasedConfig?: DailyRevealPropBasedSportConfig;
  hitStatType: string;
  defaultHitThreshold: number;
  /** Short unit label for the per-day hit, e.g. "HIT" (MLB), "PT" (NBA), "TD" (NFL). */
  hitStatLabel: string;
  ratingStatType: string;
  ratingLabel: string;
  ratingFormat: 'integer' | 'decimal1' | 'decimal3';
}

const DEFAULT_PROP_SOURCES: DailyRevealPropMarketSource[] = [
  'sportsbook_feed',
  'manual',
  'mock',
  'historical_replay',
];

const MLB_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = [
  { categoryKey: 'hit_king', categoryName: 'Hit King', propStatType: 'hitting_h', operator: 'over_or_equal' },
  { categoryKey: 'run_king', categoryName: 'Run King', propStatType: 'hitting_runs', operator: 'over_or_equal' },
  { categoryKey: 'rbi_king', categoryName: 'RBI King', propStatType: 'hitting_rbi', operator: 'over_or_equal' },
  { categoryKey: 'home_run_king', categoryName: 'Home Run King', propStatType: 'hitting_hr', operator: 'over_or_true' },
  { categoryKey: 'walk_king', categoryName: 'Walk King', propStatType: 'hitting_bb', operator: 'over_or_equal' },
  { categoryKey: 'steal_king', categoryName: 'Steal King', propStatType: 'hitting_sb', operator: 'over_or_true' },
  {
    categoryKey: 'pitcher_k_king',
    categoryName: 'Pitcher K King',
    propStatType: 'pitching_k',
    operator: 'over_or_equal',
  },
];

/*
 * Derived from the NBA week config so the day cadence and the selectable category list can
 * never drift apart — the same relationship NFL has with `NFL_DAILY_REVEAL_CONFIG` below.
 *
 * Day 7 repeats day 1's Points stat under its own category key, so this list carries two
 * entries whose `propStatType` is `points`. That is intended: a category is a DAY's round,
 * not a unique stat.
 */
const NBA_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = NBA_DAILY_REVEAL_CONFIG.days.map(
  day => ({
    categoryKey: day.categoryKey,
    categoryName: day.categoryName,
    propStatType: day.propStatType,
    operator: day.operator,
  })
);

/*
 * NCAAMB keeps the generic basketball category set it has always had, listed explicitly rather
 * than borrowing NBA's.
 *
 * It used to reuse `NBA_PROP_CATEGORIES`, which was a generic list of basketball stats. Now that
 * NBA's list is DERIVED FROM ITS SEVEN-DAY CADENCE, sharing it would silently hand college
 * basketball the NBA's Friday combo and Saturday double-double rounds — a product decision
 * nobody made. Give NCAAMB its own cadence when there is one.
 */
const NCAAMB_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = [
  { categoryKey: 'points_king', categoryName: 'Points King', propStatType: 'points', operator: 'over_or_equal' },
  {
    categoryKey: 'threes_king',
    categoryName: 'Three King',
    propStatType: 'three_points_made',
    operator: 'over_or_equal',
  },
  { categoryKey: 'rebound_king', categoryName: 'Rebound King', propStatType: 'rebounds', operator: 'over_or_equal' },
  { categoryKey: 'assist_king', categoryName: 'Assist King', propStatType: 'assists', operator: 'over_or_equal' },
  { categoryKey: 'steal_king', categoryName: 'Steal King', propStatType: 'steals', operator: 'over_or_equal' },
  { categoryKey: 'block_king', categoryName: 'Block King', propStatType: 'blocks', operator: 'over_or_equal' },
];

const SOCCER_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = [
  { categoryKey: 'goal_king', categoryName: 'Goal King', propStatType: 'soccer_goals', operator: 'over_or_true' },
  { categoryKey: 'assist_king', categoryName: 'Assist King', propStatType: 'soccer_assists', operator: 'over_or_equal' },
  {
    categoryKey: 'shots_on_target_king',
    categoryName: 'Shots On Target King',
    propStatType: 'soccer_shots_on_target',
    operator: 'over_or_equal',
  },
  { categoryKey: 'shots_king', categoryName: 'Shots King', propStatType: 'soccer_shots', operator: 'over_or_equal' },
];

const NFL_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = NFL_DAILY_REVEAL_CONFIG.days.map(day => ({
  categoryKey: day.categoryKey,
  categoryName: day.categoryName,
  propStatType: day.propStatType,
  operator: day.operator,
  gameWindow: day.gameWindow,
  lockRule: day.closeRule,
}));

export const DAILY_REVEAL_SPORT_CONFIG: Partial<Record<SportCode, DailyRevealSportConfig>> = {
  MLB: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: MLB_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'sportsbook_feed',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'hitting_h',
    defaultHitThreshold: 1,
    hitStatLabel: 'HIT',
    ratingStatType: 'batting_average',
    ratingLabel: 'AVG',
    ratingFormat: 'decimal3',
  },
  NBA: {
    /*
     * Like NFL, `sport_default` stays `stat_hit` so any existing NBA Daily Reveal contest keeps
     * its legacy points-hit gameplay. The seven-day prop cadence below is reached only by a
     * contest that sets `daily_reveal_mode = 'prop_based'` explicitly.
     */
    defaultMode: 'stat_hit',
    weekConfig: {
      type: 'nba_monday_to_sunday',
      timezone: NBA_DAILY_REVEAL_CONFIG.timezone,
      days: NBA_DAILY_REVEAL_CONFIG.days.map(day => ({
        dayNumber: day.dayNumber,
        categoryKey: day.categoryKey,
        categoryName: day.categoryName,
      })),
    },
    propBasedConfig: {
      categories: NBA_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'sportsbook_feed',
      defaultPackSize: NBA_DAILY_REVEAL_CONFIG.packSize,
      allowPickChangeBeforeLock: NBA_DAILY_REVEAL_CONFIG.allowPickChangeBeforeLock,
    },
    hitStatType: 'points',
    defaultHitThreshold: 1,
    hitStatLabel: 'PT',
    ratingStatType: 'points_per_game',
    ratingLabel: 'PPG',
    ratingFormat: 'decimal1',
  },
  NCAAMB: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: NCAAMB_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'mock',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'points',
    defaultHitThreshold: 1,
    hitStatLabel: 'PT',
    ratingStatType: 'points_per_game',
    ratingLabel: 'PPG',
    ratingFormat: 'decimal1',
  },
  NFL: {
    // sport_default must preserve legacy NFL Daily Reveal (stat-hit / TD kings).
    // Prop-based Gridiron Kings contests opt in explicitly via daily_reveal_mode = 'prop_based'.
    defaultMode: 'stat_hit',
    weekConfig: {
      type: 'nfl_tuesday_to_monday',
      timezone: NFL_DAILY_REVEAL_CONFIG.timezone,
      days: NFL_DAILY_REVEAL_CONFIG.days.map(day => ({
        dayNumber: day.dayNumber,
        categoryKey: day.categoryKey,
        categoryName: day.categoryName,
        gameWindow: day.gameWindow,
      })),
    },
    propBasedConfig: {
      categories: NFL_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'sportsbook_feed',
      defaultPackSize: NFL_DAILY_REVEAL_CONFIG.packSize,
      allowPickChangeBeforeLock: NFL_DAILY_REVEAL_CONFIG.allowPickChangeBeforeLock,
    },
    hitStatType: 'total_touchdowns',
    defaultHitThreshold: 1,
    hitStatLabel: 'TD',
    ratingStatType: 'season_touchdowns',
    ratingLabel: 'TDs',
    ratingFormat: 'integer',
  },
  SOCCER: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: SOCCER_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'mock',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'soccer_goals',
    defaultHitThreshold: 1,
    hitStatLabel: 'GOAL',
    ratingStatType: 'season_goals',
    ratingLabel: 'Goals',
    ratingFormat: 'integer',
  },
};

export function getDailyRevealConfig(sport: string | null | undefined): DailyRevealSportConfig | null {
  if (!sport) return null;
  return DAILY_REVEAL_SPORT_CONFIG[sport.toUpperCase() as SportCode] ?? null;
}

export function isDailyRevealSupportedSport(sport: string | null | undefined): boolean {
  return getDailyRevealConfig(sport) !== null;
}

export function getDefaultDailyRevealModeForSport(
  sport: string | null | undefined
): ResolvedDailyRevealMode | null {
  const config = getDailyRevealConfig(sport);
  if (!config) return null;
  return config.defaultMode ?? config.mode ?? 'stat_hit';
}

export function resolveDailyRevealMode(
  sport: string | null | undefined,
  configuredMode?: DailyRevealMode | string | null
): ResolvedDailyRevealMode {
  if (configuredMode === 'stat_hit' || configuredMode === 'prop_based') {
    return configuredMode;
  }
  return getDefaultDailyRevealModeForSport(sport) ?? 'stat_hit';
}

export function getDailyRevealWeekConfig(sport: string | null | undefined): DailyRevealWeekConfig | null {
  return getDailyRevealConfig(sport)?.weekConfig ?? null;
}

export function getDailyRevealPropBasedConfig(
  sport: string | null | undefined
): DailyRevealPropBasedSportConfig | null {
  return getDailyRevealConfig(sport)?.propBasedConfig ?? null;
}

export function getDailyRevealPropCategoryByKey(
  sport: string | null | undefined,
  categoryKey: string | null | undefined
): DailyRevealPropCategoryConfig | null {
  if (!categoryKey) return null;
  const propConfig = getDailyRevealPropBasedConfig(sport);
  return propConfig?.categories.find(category => category.categoryKey === categoryKey) ?? null;
}

export function getDefaultDailyRevealPropCategoryForDay(
  sport: string | null | undefined,
  dayNumber: number
): DailyRevealPropCategoryConfig | null {
  const config = getDailyRevealConfig(sport);
  const propConfig = config?.propBasedConfig;
  if (!config || !propConfig || propConfig.categories.length === 0) return null;

  const configuredCategoryKey = config.weekConfig?.days?.find(day => day.dayNumber === dayNumber)?.categoryKey;
  if (configuredCategoryKey) {
    return getDailyRevealPropCategoryByKey(sport, configuredCategoryKey);
  }

  return propConfig.categories[(Math.max(dayNumber, 1) - 1) % propConfig.categories.length] ?? null;
}
